import { ErrorMessage, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
} from 'react-icons/fa';
import * as Yup from 'yup';
import { boatWorldcatRear } from '../assets/images/hero/imageAssets';
import SEOHead from '../components/SEOHead';
import { getOgImageUrl, OG_IMAGE_DIMENSIONS, getContactPointSchema } from '../utils/seoSchemas';
import { ApiError, submitContactInquiry } from '../utils/apiService';
import { trackContactFormSubmission } from '../utils/analytics';
import {
  BookingCta,
  BookingCtaLink,
  ContactForm,
  ContactGrid,
  ContentContainer,
  Divider,
  ErrorBanner,
  ErrorText,
  FieldGroup,
  FieldRow,
  FormCard,
  FormSubtitle,
  FormTitle,
  HeroEyebrow,
  HeroInner,
  HeroSubtitle,
  HeroTitle,
  InfoContent,
  InfoIcon,
  InfoItem,
  InfoLabel,
  InfoPanel,
  InfoTitle,
  InfoValue,
  Label,
  PageHero,
  StyledField,
  StyledSelect,
  SubmitButton,
  SuccessCard,
  SuccessIcon,
  SuccessText,
  SuccessTitle,
} from './styles/ContactPage.styles';

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  inquiryType: string;
  message: string;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Please enter your name'),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Please enter your email'),
  phone: Yup.string(),
  inquiryType: Yup.string().required('Please select an inquiry type'),
  message: Yup.string()
    .min(10, 'Message must be at least 10 characters')
    .required('Please enter a message'),
});

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (
    values: ContactFormValues,
    { setSubmitting, resetForm }: { setSubmitting: (s: boolean) => void; resetForm: () => void }
  ) => {
    setSubmitError(null);

    try {
      await submitContactInquiry({
        name: values.name,
        email: values.email,
        phone: values.phone || undefined,
        inquiry_type: values.inquiryType,
        message: values.message,
      });

      trackContactFormSubmission(values.inquiryType);
      setSubmitted(true);
      resetForm();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422) {
          setSubmitError('Please check your form — some information appears invalid.');
        } else {
          setSubmitError(
            'There was a problem sending your inquiry. Please try again or call us at (714) 200-9815.'
          );
        }
      } else {
        setSubmitError(
          'Could not reach our server. Please try again or call us directly at (714) 200-9815.'
        );
      }
    }

    setSubmitting(false);
  };

  return (
    <>
      <SEOHead
        title="Contact Fussaki Charters — San Pedro, CA"
        description="Get in touch with Fussaki Spearfishing Charters. Ask about private charters, group bookings, availability, or anything else. Based in San Pedro, CA."
        canonical="https://fussaki.com/contact"
        ogImage={getOgImageUrl(boatWorldcatRear)}
        ogImageWidth={OG_IMAGE_DIMENSIONS.boatWorldcatRear.width}
        ogImageHeight={OG_IMAGE_DIMENSIONS.boatWorldcatRear.height}
        jsonLd={getContactPointSchema()}
      />

      {/* ── Hero ── */}
      <PageHero $bgImage={boatWorldcatRear} role="img" aria-label="WorldCat 295CC charter vessel from the stern on open water">
        <HeroInner>
          <HeroEyebrow>Contact Us</HeroEyebrow>
          <HeroTitle>Get <span>in Touch</span></HeroTitle>
          <HeroSubtitle>
            Questions about charters, availability, or private bookings? Reach out
            and Captain Christos will get back to you.
          </HeroSubtitle>
        </HeroInner>
      </PageHero>

      {/* ── Main Content ── */}
      <ContentContainer>
        <ContactGrid>
          {/* ── Left: Contact Form ── */}
          <div>
            {submitted ? (
              <SuccessCard>
                <SuccessIcon>
                  {FaCheckCircle({ size: 24 })}
                </SuccessIcon>
                <SuccessTitle>Your inquiry has been sent!</SuccessTitle>
                <SuccessText>
                  Captain Christos will be in touch shortly. Check your email for a
                  confirmation.
                </SuccessText>
              </SuccessCard>
            ) : (
              <FormCard>
                <FormTitle>Send Us a Message</FormTitle>
                <FormSubtitle>
                  Fill out the form below and we will respond within 24 hours.
                </FormSubtitle>

                <Formik<ContactFormValues>
                  initialValues={{
                    name: '',
                    email: '',
                    phone: '',
                    inquiryType: 'general',
                    message: '',
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting, setFieldValue, values }: FormikProps<ContactFormValues>) => (
                    <ContactForm>
                      <FieldRow>
                        <FieldGroup>
                          <Label htmlFor="contact-name">Name *</Label>
                          <StyledField
                            id="contact-name"
                            name="name"
                            placeholder="Your full name"
                            value={values.name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setFieldValue('name', e.target.value)
                            }
                          />
                          <ErrorMessage name="name" component={ErrorText} />
                        </FieldGroup>

                        <FieldGroup>
                          <Label htmlFor="contact-email">Email *</Label>
                          <StyledField
                            id="contact-email"
                            name="email"
                            type="email"
                            placeholder="you@example.com"
                            value={values.email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setFieldValue('email', e.target.value)
                            }
                          />
                          <ErrorMessage name="email" component={ErrorText} />
                        </FieldGroup>
                      </FieldRow>

                      <FieldRow>
                        <FieldGroup>
                          <Label htmlFor="contact-phone">Phone</Label>
                          <StyledField
                            id="contact-phone"
                            name="phone"
                            placeholder="(555) 123-4567"
                            value={values.phone}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setFieldValue('phone', e.target.value)
                            }
                          />
                        </FieldGroup>

                        <FieldGroup>
                          <Label htmlFor="contact-inquiry-type">Inquiry Type *</Label>
                          <StyledSelect
                            as="select"
                            id="contact-inquiry-type"
                            name="inquiryType"
                            value={values.inquiryType}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                              setFieldValue('inquiryType', e.target.value)
                            }
                          >
                            <option value="general">General Question</option>
                            <option value="private_charter">Private Charter Inquiry</option>
                            <option value="group_booking">Group Booking</option>
                            <option value="other">Other</option>
                          </StyledSelect>
                          <ErrorMessage name="inquiryType" component={ErrorText} />
                        </FieldGroup>
                      </FieldRow>

                      <FieldGroup>
                        <Label htmlFor="contact-message">Message *</Label>
                        <StyledField
                          as="textarea"
                          id="contact-message"
                          name="message"
                          placeholder="Tell us what you're looking for..."
                          rows={6}
                          value={values.message}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setFieldValue('message', e.target.value)
                          }
                        />
                        <ErrorMessage name="message" component={ErrorText} />
                      </FieldGroup>

                      {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

                      <SubmitButton type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            Send Inquiry {FaArrowRight({ size: 16 })}
                          </>
                        )}
                      </SubmitButton>
                    </ContactForm>
                  )}
                </Formik>
              </FormCard>
            )}
          </div>

          {/* ── Right: Info Panel ── */}
          <InfoPanel>
            <InfoTitle>Contact Info</InfoTitle>

            <InfoItem>
              <InfoIcon>{FaPhoneAlt({ size: 14 })}</InfoIcon>
              <InfoContent>
                <InfoLabel>Phone</InfoLabel>
                <InfoValue>
                  <a href="tel:7142009815">(714) 200-9815</a>
                </InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>{FaEnvelope({ size: 14 })}</InfoIcon>
              <InfoContent>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>
                  <a href="mailto:fussaki@gmail.com">fussaki@gmail.com</a>
                </InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>{FaMapMarkerAlt({ size: 14 })}</InfoIcon>
              <InfoContent>
                <InfoLabel>Address</InfoLabel>
                <InfoValue>224 Whalers Walk, San Pedro, CA 90731</InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>{FaClock({ size: 14 })}</InfoIcon>
              <InfoContent>
                <InfoLabel>Hours</InfoLabel>
                <InfoValue>Daily, 5:30 AM - 6:00 PM (trip days)</InfoValue>
              </InfoContent>
            </InfoItem>

            <InfoItem>
              <InfoIcon>{FaInstagram({ size: 14 })}</InfoIcon>
              <InfoContent>
                <InfoLabel>Instagram</InfoLabel>
                <InfoValue>
                  <a
                    href="https://instagram.com/fussaki/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    @fussaki
                  </a>
                </InfoValue>
              </InfoContent>
            </InfoItem>

            <Divider />

            <BookingCta>
              For immediate booking, visit our{' '}
              <BookingCtaLink to="/book">booking page</BookingCtaLink>.
            </BookingCta>
          </InfoPanel>
        </ContactGrid>
      </ContentContainer>
    </>
  );
};

export default ContactPage;
